import React, { useEffect, useMemo, useState } from 'react'

import { useAccountActions } from 'redux/actions/Accounts'

import { EAccountRole } from 'types/GlobalAccount'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useGroups } from 'redux/hooks/Groups'
import { useTranslation } from 'language/translator'
import { useRoles } from 'redux/hooks/Accounts'
import { useCompanyGroups } from 'redux/hooks/CompanyGroups'
import {
  useAllowInternalFollowUp,
  useIsManagementCompany
} from 'redux/hooks/Customer'
import { useOrganizationalUnitsArray } from 'redux/hooks/OrganizationalUnits'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import InputAdornment from 'components_new/atoms/InputAdornment'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import TextField from 'components_new/atoms/TextField'

import Banner from 'components_new/molecules/Banner'
import SelectSearch, { Option } from 'components_new/molecules/SelectSearch'

import TextInput, {
  useTextInputState
} from 'components_new/molecules/TextInput'
import TabSwitch from 'components_new/molecules/TabSwitch'

import SelectUserRole from 'components_new/organisms/SelectUserRole'

import { formatGroupOption } from 'utils/functions'

enum SwitchTabValues {
  COLLEAGUE = 'COLLEAGUE',
  COMPANY_GROUP = 'COMPANY_GROUP'
}

const translateSwitchTabValues: Record<
  SwitchTabValues,
  'colleague' | 'company_group_user'
> = {
  [SwitchTabValues.COLLEAGUE]: 'colleague',
  [SwitchTabValues.COMPANY_GROUP]: 'company_group_user'
}

interface AddAccountProps {
  companyGroupId?: string
  hideGroups?: boolean
  hideNames?: boolean
  hideRole?: boolean
  sx?: object
}

const ALL_UNITS = 'ALL_UNITS'

const AddAccount = (props: AddAccountProps) => {
  const {
    companyGroupId,
    hideGroups = false,
    hideNames = false,
    hideRole = false,
    sx
  } = props

  const { t } = useTranslation()

  const { tryCreateUser } = useAccountActions()
  const { roles } = useRoles()
  const isManagementCompany = useIsManagementCompany()
  const allowInternalFollowUp = useAllowInternalFollowUp()
  const [companyGroups] = useCompanyGroups()
  const { allowAccess, hasIdp: userHasIdp, isHomepal } = useAccountSettings()
  // special check for homepal users
  const hasIdp = userHasIdp && !isHomepal

  // get group options
  const { allGroups } = useGroups()
  const groupOptions = useMemo(() => {
    return allGroups.map((group) => formatGroupOption(group, t))
  }, [allGroups])
  const organizationalUnits = useOrganizationalUnitsArray()

  const [dialogTab, setDialogTab] = useState<SwitchTabValues>(
    SwitchTabValues.COLLEAGUE
  )
  const email = useTextInputState()
  const message = useTextInputState()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [role, setRoleState] = useState<EAccountRole>(EAccountRole.VIEW)
  const [allowEconomy, setAllowEconomy] = useState(false)
  const [allowAnalyses, setAllowAnalyses] = useState(false)
  const [organizationalUnitId, setOrganizationalUnitId] =
    useState<string>(ALL_UNITS)
  const [selectedCompanyGroup, setSelectedCompanyGroup] = useState<{
    id: string
    title: string
  } | null>(null)
  const [selectedGroups, setSelectedGroups] = useState<Option[]>([])

  const setRole = (newRole: EAccountRole) => {
    if (newRole === EAccountRole.ADMIN) {
      setOrganizationalUnitId(ALL_UNITS)
    }

    setRoleState(newRole)
  }

  // If company group id is set, use this.
  useEffect(() => {
    if (companyGroupId) {
      const companyGroup = companyGroups.find(
        (group) => group.id === companyGroupId
      )

      setDialogTab(SwitchTabValues.COMPANY_GROUP)

      if (companyGroup) {
        setSelectedCompanyGroup({
          id: companyGroup.id,
          title: companyGroup.name
        })
      }
    } else {
      setSelectedCompanyGroup(null)
    }
  }, [companyGroupId])

  const emptyFirstName = firstName.length === 0
  const emptyLastName = lastName.length === 0
  const validCompanyGroup =
    dialogTab === SwitchTabValues.COLLEAGUE || !!selectedCompanyGroup
  const validEmail = !email.error && Boolean(email.value)

  const [showErrors, setShowErrors] = useState(false)

  const validFields =
    validEmail &&
    (hideNames || (!emptyFirstName && !emptyLastName)) &&
    validCompanyGroup

  const validate = () => {
    if (validFields) {
      setShowErrors(false)

      return true
    } else {
      setShowErrors(true)

      return false
    }
  }

  const handleSubmit = () => {
    if (validate()) {
      const userGroups =
        hideGroups || selectedCompanyGroup
          ? []
          : selectedGroups.map((g) => g.id as string)

      tryCreateUser({
        data: {
          email: email.value,
          first_name: firstName,
          groups: userGroups,
          last_name: lastName,
          message: message.value,
          role: roles[role].id,
          company_group_id: selectedCompanyGroup?.id as string | undefined,
          has_access_to_economy_settings: allowEconomy,
          allow_access_to_analyses: allowAnalyses,
          organizational_unit_id:
            organizationalUnitId === ALL_UNITS ? null : organizationalUnitId
        }
      })

      email.setValue('')
      setFirstName('')
      setLastName('')
      message.setValue('')
      setSelectedGroups([])
      setRole(EAccountRole.VIEW)
      setSelectedCompanyGroup(null)
      setOrganizationalUnitId(ALL_UNITS)
    }
  }

  const inviteContent = (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', ...sx }}>
      {hideNames ? null : (
        <Box sx={{ width: '100%', display: 'flex', gap: 1 }}>
          <TextField
            label={t('users', 'first_name')}
            required={true}
            autoComplete="off"
            error={showErrors && emptyFirstName}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            helperText={
              showErrors && emptyFirstName
                ? t('users', 'first_name_missing_error')
                : undefined
            }
            sx={{ marginTop: 0, marginBottom: 0 }}
          />
          <TextField
            label={t('users', 'last_name')}
            required={true}
            autoComplete="off"
            error={showErrors && emptyLastName}
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            helperText={
              showErrors && emptyLastName
                ? t('users', 'last_name_missing_error')
                : undefined
            }
            sx={{ marginTop: 0, marginBottom: 0 }}
          />
        </Box>
      )}
      <TextInput
        autoComplete="off"
        required={true}
        variant="email"
        {...email}
      />
      {hideRole || dialogTab === SwitchTabValues.COMPANY_GROUP ? null : (
        <SelectUserRole
          allowEconomy={allowEconomy}
          allowAnalyses={allowAnalyses}
          role={role}
          setAllowEconomy={setAllowEconomy}
          setAllowAnalyses={setAllowAnalyses}
          setRole={setRole}
          showAnalyses={allowAccess.allow_analyses}
        />
      )}
      {hideGroups ||
      dialogTab === SwitchTabValues.COMPANY_GROUP ||
      !allowInternalFollowUp ? null : (
          <>
            <TextField
              disabled={role === EAccountRole.ADMIN}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon name="Access" />
                  </InputAdornment>
                )
              }}
              label={t('data_access', 'data_access')}
              select={true}
              value={organizationalUnitId}
            >
              <MenuItem
                value={ALL_UNITS}
                onClick={() => setOrganizationalUnitId(ALL_UNITS)}
              >
                {t('global', 'all')}
              </MenuItem>
              {organizationalUnits.map((unit) => {
                return (
                  <MenuItem
                    value={unit.id}
                    key={unit.id}
                    onClick={() => setOrganizationalUnitId(unit.id)}
                  >
                    {unit.name}
                  </MenuItem>
                )
              })}
            </TextField>
            <SelectSearch
              label={t('global', 'groups')}
              multiple={true}
              selected={selectedGroups}
              onChange={(options: Option[] | null) =>
                options ? setSelectedGroups(options) : setSelectedGroups([])
              }
              options={groupOptions}
            />
          </>
        )}

      {dialogTab === SwitchTabValues.COMPANY_GROUP ? (
        <SelectSearch
          error={showErrors && !validCompanyGroup}
          helperText={
            showErrors && !validCompanyGroup
              ? t('users', 'company_group_missing_error')
              : undefined
          }
          label={`${t('global', 'company_group')}*`}
          disableClearable={true}
          multiple={false}
          noGutter={true}
          onChange={(option) => setSelectedCompanyGroup(option)}
          options={companyGroups.map((cg) => ({ id: cg.id, title: cg.name }))}
          PopperWidth={300}
          selected={selectedCompanyGroup}
          size={'medium'}
          disabled={!!companyGroupId}
        />
      ) : null}

      <Collapse in={validFields}>
        <TextInput
          label={t('users', 'message')}
          autoComplete="off"
          variant="multiline"
          sx={{ my: 0 }}
          {...message}
        />
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('users', 'message_information')}
        </Alert>
      </Collapse>
      <Button fullWidth variant="contained" size="large" onClick={handleSubmit}>
        {t('global', 'invite')}
      </Button>
    </Box>
  )

  return (
    <>
      <Collapse in={isManagementCompany && !companyGroupId}>
        <TabSwitch
          options={Object.values(SwitchTabValues).map((value) => ({
            value,
            label: t('users', translateSwitchTabValues[value])
          }))}
          value={dialogTab}
          onChange={(value) => {
            if (value === SwitchTabValues.COLLEAGUE) {
              setSelectedCompanyGroup(null)
            }
            if (value === SwitchTabValues.COMPANY_GROUP) {
              setRole(EAccountRole.VIEW)
            }

            setDialogTab(value as SwitchTabValues)
          }}
          color={'primary'}
          sx={{ mt: -2, mb: 2 }}
        />
      </Collapse>
      {hasIdp && dialogTab === SwitchTabValues.COLLEAGUE ? (
        <Banner
          title={t('users', 'sso_title')}
          sx={{ bgcolor: 'warning.main' }}
          body={t('users', 'sso_create_error')}
          iconName={'WarningOutlined'}
        />
      ) : (
        inviteContent
      )}
    </>
  )
}

export default AddAccount
